import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppState } from '../state/app.state';
import { ESignFlowParameters } from '../models/esignflow-parameters.model';
import { DigitalSigningParameters } from '../models/digital-signing-parameters.model';
import { DocumentServiceParameters } from '../models/document-service-parameters.model';
import { Response } from '../models/response.model';
import { ESignflowTemplate } from '../models/esignflow-template.model';
import { DocumentWorkflowDetailsVM } from '../models/document-workflow-details.model';
import { StartWorkflowRequest } from '../models/document-workflow-start.model';
import { DocumentWorkflowType } from '../models/document-workflow';
import { DocumentWorkflowListVM } from '../models/document-workflow-list.model';
import { FileStorageFile } from '../models/file-storage-file.model';
import { DocumentWorkflowTask } from '../models/document-workflow-task.model';

const httpOptions = {
  headers: new HttpHeaders({ "Content-Type": "application/json" }),
};

@Injectable({
  providedIn: 'root'
})
export class DocumentWorkflowService {
  constructor(private http: HttpClient, private appState: AppState) {} 

  enableExplicitParameters(letterId: number, type: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.appState.baseApiUrl}/documentworkflow/parameters/${letterId}/${type}`, httpOptions);
  }

  getParametersESignflow(letterId = 0): Observable<ESignFlowParameters> {
    return this.http.get<ESignFlowParameters>(`${this.appState.baseApiUrl}/documentworkflow/parameters/1?letterid=${letterId}`, httpOptions);
  }
  
  updateParametersESignflow(parameters: ESignFlowParameters, groupId: string = null): Observable<boolean> {
    return this.http.put<boolean>(`${this.appState.baseApiUrl}/documentworkflow/parameters/esignflow?groupid=${groupId}`, parameters, httpOptions);
  }
  
  getESignflowTemplates(): Observable<ESignflowTemplate[]> {
    return this.http.get<ESignflowTemplate[]>(`${this.appState.baseApiUrl}/documentworkflow/parameters/esignflow/templates`, httpOptions);
  }

  getParametersDigitalSigning(letterId: number = 0): Observable<DigitalSigningParameters[]> {
    return this.http.get<DigitalSigningParameters[]>(`${this.appState.baseApiUrl}/documentworkflow/parameters/2?letterid=${letterId}`, httpOptions);
  }

  updateParametersDigitalSigning(signer: DigitalSigningParameters, letterId: number): Observable<boolean> {
    return this.http.put<boolean>(`${this.appState.baseApiUrl}/documentworkflow/parameters/digitalsigner?letterId=${letterId}`, signer, httpOptions);
  }

  bulkUpdateParametersDigitalSigning(parameters: DigitalSigningParameters[], letterId: number): Observable<boolean> {
    return this.http.put<boolean>(`${this.appState.baseApiUrl}/documentworkflow/parameters/digitalsigner/bulk?letterId=${letterId}`, parameters, httpOptions);
  }

  deleteParametersByWorkflowGroup(group: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.appState.baseApiUrl}/documentworkflow/parameters/${group}`, httpOptions);
  }

  deleteParametersWorkflowByType(type: number, letterId: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.appState.baseApiUrl}/documentworkflow/parameters/${type}/${letterId}`, httpOptions);
  }

  updateParametersDocumentService(documentServiceParameters: DocumentServiceParameters, letterId: number = 0): Observable<boolean> {
    return this.http.put<boolean>(`${this.appState.baseApiUrl}/documentworkflow/parameters/documentservice?letterid=${letterId ?? 0}`, documentServiceParameters, httpOptions);
  }

  getParametersDocumentService(letterId: number = 0): Observable<DocumentServiceParameters> {
    return this.http.get<DocumentServiceParameters>(`${this.appState.baseApiUrl}/documentworkflow/parameters/3?letterid=${letterId}`, httpOptions);
  }

  getResultFiles(gebCode: string, contextType: string, contextCode: string): Observable<FileStorageFile[]> {
    return this.http.get<FileStorageFile[]>(`${this.appState.baseApiUrl}/documentworkflow/${gebCode}/${contextType}/${contextCode}/files`, httpOptions);
  }

  getList(gebCode: string, contextType: string, contextCode: string): Observable<DocumentWorkflowListVM[]> {
    return this.http.get<DocumentWorkflowListVM[]>(`${this.appState.baseApiUrl}/documentworkflow/${gebCode}/${contextType}/${contextCode}/list`, httpOptions);
  }

  getDetail(gebCode: string, contextType: string, contextCode: string, workflowId: number): Observable<Response> {
    return this.http.get<Response>(`${this.appState.baseApiUrl}/documentworkflow/${gebCode}/${contextType}/${contextCode}/${workflowId}`, httpOptions);
  }

  startWorkflow(request: StartWorkflowRequest): Observable<Response> {
    switch (request.Type) {
      case DocumentWorkflowType.ESIGNFLOW:
        return this.http.post<Response>(`${this.appState.baseApiUrl}/documentworkflow/start/esignflow`, request, httpOptions);

      case DocumentWorkflowType.MAGDA_DIGITAL_SIGNING:
        return this.http.post<Response>(`${this.appState.baseApiUrl}/documentworkflow/start/digitalsigning`, request, httpOptions);

      case DocumentWorkflowType.MAGDA_DOCUMENT_SERVICE:
        return this.http.post<Response>(`${this.appState.baseApiUrl}/documentworkflow/start/documentservice`, request, httpOptions);
    }
  }

  cancelWorkflow(gebCode: string, contextType: string, contextCode: string, workflowId: number): Observable<Response> {
    return this.http.delete<Response>(`${this.appState.baseApiUrl}/documentworkflow/${gebCode}/${contextType}/${contextCode}/${workflowId}`, httpOptions);
  }

  deleteWorkflowById(gebCode: string, contextType: string, contextCode: string, workflowId: number): Observable<Response> {
    return this.http.delete<Response>(`${this.appState.baseApiUrl}/documentworkflow/${gebCode}/${contextType}/${contextCode}/${workflowId}/delete`, httpOptions);
  }

  checkHasEboxEnabled(gebCode: string, ownerCode: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.appState.baseApiUrl}/documentworkflow/documentservice/eboxpermissions/${gebCode}/${ownerCode}`, httpOptions);
  }

  updateStatusses(gebCode: string): Observable<Response> {
    return this.http.get<Response>(`${this.appState.baseApiUrl}/documentworkflow/update-statusses/${gebCode}`, httpOptions);
  }

  getDetailsBySharepointId(sharepointId: string): Observable<DocumentWorkflowDetailsVM[]> {
    return this.http.get<DocumentWorkflowDetailsVM[]>(`${this.appState.baseApiUrl}/documentworkflow/details/${sharepointId}`, httpOptions);
  }

  getScheduleList(): Observable<DocumentWorkflowTask[]> {
    return this.http.get<DocumentWorkflowTask[]>(`${this.appState.baseApiUrl}/documentworkflow/schedule/list`, httpOptions);
  }

  cancelScheduled(scheduleId: number): Observable<Response> {
    return this.http.delete<Response>(`${this.appState.baseApiUrl}/documentworkflow/schedule/${scheduleId}`, httpOptions);
  }

  sendScheduled(scheduleId: number): Observable<Response> {
    return this.http.post<Response>(`${this.appState.baseApiUrl}/documentworkflow/schedule/${scheduleId}/send`, {}, httpOptions);
  }
}